import {
  Dispatch, Fragment, ReactElement, SetStateAction, useCallback, useEffect, useMemo, useState,
} from 'react';
import { ExtendedSWRInfiniteResponse, InfinitePageType, PaginationPageType } from '@type/swr';
import { clsx } from 'clsx';
import { CardJobTalentMemo } from '@components/templates/job/Card/CardJobTalent';
import {
  Button, InfiniteList, Modal, PageCounter,
} from '@components/common';
import { useResizeObserver } from '@hooks/useResizeObserver';
import { JobListsContext } from '@components/contexts/JobListsContext';
import { JobDetailHub } from '@components/templates/job/JobDetailHub';
import { TFunction } from 'next-i18next';
import { JobListType } from '@type/job-params';
import { JobTrackingContext } from '@components/contexts/JobTrackingContext';
import { EmptyJobList } from '@components/templates/job/list/EmptyJobList';
import { useUrlChange } from '@utils/url/useUrlChange';
import { JOB_LIST_ITEMS_PER_PAGE } from '@root/src/constants';
import { BaseTrackingEventData } from '@type/tracking';

interface WithJobItems {
  items?: ApiSchema<'JobItem2'>[];
  page?: number;
  nextCursor?: string;
  apiRequestId?: string;
  searchId?: string;
}

// Interface
// ******************************************
type PickedSwrHookProps<T extends WithJobItems = ApiSchema<'JobResult'>> = Omit<ExtendedSWRInfiniteResponse<T, unknown>, | 'error' | 'mutate' | 'mutateSiblings' | 'isValidating' | 'mutatePage'>

interface JobCardListProps<T extends WithJobItems = ApiSchema<'JobResult'>, P extends InfinitePageType = 'page'> extends PickedSwrHookProps<T> {
  t: TFunction,
  setCurrentPage: Dispatch<SetStateAction<PaginationPageType<P>>>,
  locale: Locale,
  autoload?: number,
  isValidating?: boolean,
  listType?: JobListType,
  renderBanner?: (position: number) => ReactElement | null,
  trackingData? : BaseTrackingEventData;
  title?: string,
  isEmptyLabels?: {
    isEmptyTitle?: string,
    isEmptyText?: string,
  },
  paginationType?: P,
  forceModalOnMc?: boolean,
  useCurrentUrlForModal?: boolean,
}

/**
 * @description JobCardList Component.
 * This component renders a list of job cards with infinite scrolling and opens the job detail in a modal.
 * To be used for all job lists (job listing, similar, favorites, blocked, etc.) where the data model is `ApiSchema<'JobResult'>`.
 *
 * - **t**: TFunction - the translation function
 * - **data**: Array of objects extending `WithJobItems` - the data to be displayed in the list. - Default is `ApiSchema<'JobResult'>`.
 * - **setSize**: Dispatch<SetStateAction<number>> - the function to set the size of the data. (get it from the SwrInfinite hook)
 * - **size**: number - the current size of the data (get it from the SwrInfinite hook)
 * - **setCurrentPage**: Dispatch<SetStateAction<string | number>> - the function to set the current page inside useFiltersQueryParams depending on the pagination type.
 * - **showLoadMore**: boolean - a flag to show the load more button (get it from the SwrInfinite hook)
 * - **isEmpty**: boolean - a flag indicating if the list is empty (get it from the SwrInfinite hook)
 * - **isLoading**: boolean - a flag indicating if the list is loading (get it from the SwrInfinite hook)
 * - **isValidating**: boolean - a flag indicating if the *data* is being revalidated (get it from the SwrInfinite hook)
 * - **sizeAsRef**: RefObject<number> - is the ref for the size of the infinite list (get it from the SwrInfinite hook)
 * - **autoload**: number - the number of pages to autoload. Default is 3.
 * - **listType**: number - the job list type, from `JobListType`. Used for tracking purposes.
 * - **renderBanner**: (viewportPage: number, index: number) => ReactElement | null - a function that can be used to display a banner between the job cards in the list (eg: job alert card).
 * It receives as parameters the current page and the item index.
 * - **title**: string - the title of the job list, containing the current filter labels. It will be passed to EmptyStateComponent.
 * - **paginationType**: InfinitePageType - the type of pagination used. Default is 'page'.
 * - **forceModalOnMc**: boolean - if true, cards on MC routes open the job detail modal (with close + prev/next
 * navigation) instead of routing to the standalone /mc/job/[slug] page.
 * - **useCurrentUrlForModal**: boolean - if true, opening the modal keeps the current page path and only adds
 * selectedJobSlug. Required on MC routes, where a pathname change would swap the URL-driven shell view
 * and unmount the list (and the modal with it).
 *
 */
export const JobCardList = <T extends WithJobItems = ApiSchema<'JobResult'>, P extends InfinitePageType = 'page'>(props: JobCardListProps<T, P>) => {
  // Destructure props
  const {
    t,
    data,
    trackingData,
    setSize,
    size,
    setCurrentPage,
    showLoadMore,
    isEmpty,
    isLoading,
    isValidating,
    sizeAsRef,
    autoload = 3,
    listType,
    renderBanner,
    title,
    isEmptyLabels,
    locale,
    paginationType = 'page' as P,
    forceModalOnMc = false,
    useCurrentUrlForModal = false,
  } = props;

  // State to store the selected job slug from the URL
  const [selectedJobSlugQueryParam, setSelectedJobSlug] = useState<string | null>(null);
  const [appliedJobs, setAppliedJobs] = useState<Record<string, string>>({});

  const markJobApplied = useCallback((jobSlug: string) => {
    if (!jobSlug) return;

    setAppliedJobs((current) => {
      if (current[jobSlug]) return current;

      return {
        ...current,
        [jobSlug]: new Date().toISOString(),
      };
    });
  }, []);

  const withAppliedState = useCallback(<J extends { slug?: string; appliedAt?: string | null }>(jobItem: J): J => {
    const appliedAt = jobItem.slug ? appliedJobs[jobItem.slug] : undefined;

    if (!appliedAt || jobItem.appliedAt) {
      return jobItem;
    }

    return {
      ...jobItem,
      appliedAt,
    };
  }, [appliedJobs]);

  // Function to check if the selectedJobSlug exists in the URL
  const checkSelectedJobSlug = (url: string) => {
    // If the URL is relative, combine it with the current origin to make it absolute
    const absoluteUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;

    // Create a new URL object
    const { searchParams } = new URL(absoluteUrl);

    // Return the value of the 'selectedJobSlug' parameter, or null if it's not found
    return searchParams.get('selectedJobSlug');
  };

  // Detect URL changes and check for selectedJobSlug
  useUrlChange((newUrl: string) => {
    const jobSlug = checkSelectedJobSlug(newUrl);
    setSelectedJobSlug(jobSlug); // Update the state with the slug
  });

  // On initial mount, open the modal if the shared/refreshed URL already carries selectedJobSlug.
  // useUrlChange only fires on subsequent URL changes, so a directly-loaded link would otherwise
  // render the list without its job-detail modal.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setSelectedJobSlug(params.get('selectedJobSlug'));
  }, []);


  // Resize observer. Need to watch the container for the infinite list in order to do proper calculations for pagination.
  // *************************************
  const { ref: parentRef, width: parentWidth } = useResizeObserver<HTMLDivElement>({
    round: Math.floor,
  });

  // Current list URL
  // *************************************
  const [currentListUrl, setCurrentListUrl] = useState<string | undefined>(undefined);

  // Job Detail Modal helpers
  // *************************************
  const closeJobModal = () => {
    // Opened from a list (card click): currentListUrl holds the list URL captured before the
    // modal opened — return to it.
    if (currentListUrl) {
      // Use window.history.replaceState to update the URL without adding a new history entry
      window.history.replaceState(null, '', currentListUrl);
      return;
    }

    // Opened directly (shared / refreshed link): there is no in-app list URL to return to.
    // Strip selectedJobSlug from the current URL to close the modal and reveal the underlying
    // list (the /mc/job/list/:keyword route is already mounted beneath the modal).
    const url = new URL(window.location.href);
    url.searchParams.delete('selectedJobSlug');
    window.history.replaceState(null, '', `${url.pathname}${url.search}`);
  };

  // Data for the Job Lists Context.
  // ***********************************************
  const jobListsContext = useMemo(() => ({
    swrJobList: data,
    showLoadMore,
    size,
    setSize,
    isLoading,
    onJobApplied: markJobApplied,
  }), [data, size, setSize, showLoadMore, isLoading, markJobApplied]);

  // Data for the Job Tracking Context.
  // ***********************************************
  const jobTrackingContext = useMemo(() => ({
    requestId: data?.at(-1)?.apiRequestId,
    listType,
    searchId: data?.at(-1)?.searchId,
  }), [data, listType]);

  // Helper variables
  // *************************************
  const isRefreshing = useMemo(
    () => isValidating && !isLoading,
    [isValidating, isLoading],
  );

  // Render component
  // *************************************
  return (
    <>
      <JobTrackingContext.Provider value={jobTrackingContext}>

        {/* Infinite list */}
        <InfiniteList
          autoload={autoload}
          infiniteProps={{
            setSize,
            isEmpty,
            showLoadMore,
            sizeAsRef,
            isLoading,
          }}
          isEmptyLabels={isEmptyLabels}
          renderList={() => (
            <div ref={parentRef} className={clsx('relative mb-8 grid auto-rows-auto gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-6', { 'opacity-80 saturate-50': isRefreshing })}>
              { data?.map((pageGroup, pageGroupIndex) => {
                // const viewportPage = pageGroup?.page || 1;
                let viewportPage: PaginationPageType<P>;
                if (paginationType === 'cursor') {
                  // When cursor pagination is used, the cursor for current page was contained by the previous page
                  const prevPageCursor = pageGroupIndex === 0 ? null : data[pageGroupIndex - 1].nextCursor;
                  viewportPage = (prevPageCursor ?? '') as PaginationPageType<P>;
                } else {
                  viewportPage = (pageGroup.page ?? 1) as PaginationPageType<P>;
                }
                return (
                  <PageCounter
                    pageGroup={pageGroup}
                    currentPage={viewportPage}
                    callback={setCurrentPage}
                    key={`page-${viewportPage}`}
                    parentWidth={parentWidth}
                    paginationType={paginationType}
                  >
                    {pageGroup?.items?.map((jobItem, index) => {
                      const jobPosition = (pageGroupIndex * JOB_LIST_ITEMS_PER_PAGE) + index + 1;
                      const jobItemWithAppliedState = withAppliedState(jobItem);
                      return (
                        renderBanner && renderBanner(jobPosition) ? (
                          <Fragment key={jobItem.id}>
                            <CardJobTalentMemo
                              job={jobItemWithAppliedState}
                              position={jobPosition}
                              openInModal
                              forceModalOnMc={forceModalOnMc}
                              useCurrentUrlForModal={useCurrentUrlForModal}
                              locale={locale}
                              trackingData={{
                                disableTracking: !!selectedJobSlugQueryParam,
                                ...(trackingData?.listType && { listType: trackingData?.listType }),
                                ...(trackingData?.listUuid && { listUuid: trackingData?.listUuid }),
                                position: jobPosition,
                              }}
                              beforeModalOpen={() => {
                                setCurrentListUrl(window.location.href);
                              }}
                              isApplication={listType === JobListType.APPLICATION_LIST}
                            />
                            {renderBanner(jobPosition)}
                          </Fragment>
                        ) : (
                          <CardJobTalentMemo
                            key={jobItem.id}
                            job={jobItemWithAppliedState}
                            position={jobPosition}
                            openInModal
                            forceModalOnMc={forceModalOnMc}
                            useCurrentUrlForModal={useCurrentUrlForModal}
                            trackingData={{
                              disableTracking: !!selectedJobSlugQueryParam,
                              ...(trackingData?.listType && { listType: trackingData?.listType }),
                              ...(pageGroup?.apiRequestId && { listUuid: pageGroup?.apiRequestId }),
                              position: jobPosition,
                            }}
                            locale={locale}
                            beforeModalOpen={() => {
                              setCurrentListUrl(window.location.href);
                            }}
                            isApplication={listType === JobListType.APPLICATION_LIST}
                          />
                        )
                      );
                    })}
                  </PageCounter>
                );
              })}

              {(isRefreshing) && (
                <div className="absolute inset-0 z-2 cursor-wait backdrop-saturate-50" />
              )}
            </div>
          )}
          renderButton={() => (
            <div className="my-8 text-center">
              <Button
                isLoading={isLoading}
                rounding="full"
                color="secondary"
                onClick={() => { void setSize(size + 1); }}
              >
                {t('global.label.load_more')}
              </Button>
            </div>
          )}
          renderEmptyState={() => <EmptyJobList title={title} />}
        />

        {/* Job Detail Modal */}
        {typeof selectedJobSlugQueryParam === 'string' && selectedJobSlugQueryParam && (
          <JobListsContext.Provider value={jobListsContext}>
            <Modal
              isOpen
              onClose={closeJobModal}
              size="full"
              containerClasses="overflow-x-hidden"
              className="transform-none!"
              body={(
                <JobDetailHub
                  jobSlug={selectedJobSlugQueryParam}
                  closeModal={closeJobModal}
                  locale={locale}
                />
              )}
            />
          </JobListsContext.Provider>
        )}
      </JobTrackingContext.Provider>
    </>
  );
};
